import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from './useDebounce';

export interface RosterFilters {
  category?: number;
  activeOnly: boolean;
  sortBy: 'last_name' | 'first_name' | 'ni_number' | 'inserted_date';
  sortOrder: 'asc' | 'desc';
}

const DEFAULTS: RosterFilters = {
  activeOnly: true,
  sortBy: 'last_name',
  sortOrder: 'asc',
};

export function useRosterSearch() {
  const [params, setParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState(params.get('q') || '');
  const [filters, setFilters] = useState<RosterFilters>({
    category: params.get('category')
      ? Number(params.get('category'))
      : undefined,
    activeOnly: params.get('active') !== 'false',
    sortBy:
      (params.get('sort') as RosterFilters['sortBy']) || 'last_name',
    sortOrder: (params.get('order') as 'asc' | 'desc') || 'asc',
  });

  const debouncedSearch = useDebounce(search, 300);

  // Sync state to URL params
  useEffect(() => {
    const p = new URLSearchParams();
    if (debouncedSearch) p.set('q', debouncedSearch);
    if (filters.category) p.set('category', String(filters.category));
    if (!filters.activeOnly) p.set('active', 'false');
    if (filters.sortBy !== 'last_name') p.set('sort', filters.sortBy);
    if (filters.sortOrder !== 'asc') p.set('order', filters.sortOrder);
    setParams(p, { replace: true });
  }, [debouncedSearch, filters, setParams]);

  // Cmd/Ctrl+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return {
    search,
    setSearch,
    debouncedSearch,
    filters,
    updateFilters: useCallback(
      (update: Partial<RosterFilters>) =>
        setFilters((prev) => ({ ...prev, ...update })),
      []
    ),
    inputRef,
    clearAll: useCallback(() => {
      setSearch('');
      setFilters(DEFAULTS);
    }, []),
    hasActiveFilters: Boolean(
      search || filters.category || filters.sortBy !== 'last_name'
    ),
  };
}
